import {
    Breakpoints,
    CTA,
    Text,
    TextAlignment,
    TextTag,
    TokenTextAppearance,
} from '@volkswagen-onehub/components-core'
import styled from 'styled-components'

import React, {useEffect, useState} from 'react'
import {useSelector} from 'react-redux'
import {RootState} from '../rootReducer'
import isEmpty from 'lodash.isempty'

import {PromotionsComponent} from '../components/PromotionsComponent'

import {capitalizeWords, removeCompanySuffix} from '../../shared/utilities'
import {DealerData} from '../../types/dealer'
import {useTranslation} from 'react-i18next'
import {Loading} from '../components/loading'

const HeadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${(props) => props.theme.size.static350};
    margin: auto;
    align-items: center;

    @media (min-width: ${Breakpoints.b960}px) {
        width: ${(props) => props.theme.size.grid018};
    }

    @media (min-width: ${Breakpoints.b1280}px) {
        width: ${(props) => props.theme.size.grid012};
    }
`
const PromotionsTeaserContainer = styled.div`
    margin: ${(props) => props.theme.size.dynamic0270} 0;
`
const CTAAllPromotions = styled(CTA)`
    width: fit-content;
`

export function TeaserView(): React.ReactElement {
    const {t} = useTranslation('fa-coupon')

    const loading = useSelector((state: RootState) => state.general.loading)
    const [showAllPromotions, setShowAllPromotions] = useState(false)

    const myDealerCode = useSelector(
        (state: RootState) => state.dealers.myDealerCode
    )

    const myDealerBranches = useSelector(
        (state: RootState) => state.dealers.myDealerBranches
    )

    const myDealerPromotions = useSelector(
        (state: RootState) => state.dealers.myDealerPromotions
    )

    const allPromotions = useSelector(
        (state: RootState) => state.promotions.allPromotions
    )

    const allPromotionsLoaded = useSelector(
        (state: RootState) => state.promotions.allPromotionsLoaded
    )

    useEffect(() => {
        setShowAllPromotions(false)
    }, [myDealerCode])

    // The branch search is the only check that the MyDealer belongs to the
    // network: the dealer promotions endpoint answers for any code.
    const myDealer: DealerData | undefined = myDealerCode
        ? myDealerBranches.find(
              (dealerData: DealerData) =>
                  dealerData.companyCode === myDealerCode
          )
        : undefined
    const dealerName = myDealer?.company
        ? capitalizeWords(removeCompanySuffix(myDealer.company))
        : ''

    if (loading || !allPromotionsLoaded) {
        return <Loading />
    }

    return (
        <>
            {dealerName && !showAllPromotions ? (
                <>
                    {!isEmpty(myDealerPromotions) ? (
                        <>
                            <HeadingContainer>
                                <Text
                                    tag={TextTag.h2}
                                    appearance={TokenTextAppearance.headline300}
                                    textAlign={TextAlignment.center}
                                >
                                    <span>
                                        {t(
                                            'teaserView.title.myDealer.availablePromotions'
                                        )}{' '}
                                        <b>{dealerName}</b>
                                    </span>
                                </Text>
                            </HeadingContainer>
                            <PromotionsTeaserContainer>
                                <PromotionsComponent
                                    promotions={myDealerPromotions}
                                    headingTag={TextTag.h3}
                                />
                            </PromotionsTeaserContainer>
                            <HeadingContainer>
                                {allPromotions.length >
                                myDealerPromotions.length ? (
                                    <CTAAllPromotions
                                        tag="button"
                                        emphasis="secondary"
                                        title={t('teaserView.cta.text')}
                                        aria-label={t('teaserView.cta.text')}
                                        onClick={() => {
                                            setShowAllPromotions(true)
                                        }}
                                    >
                                        {t('teaserView.cta.text')}
                                    </CTAAllPromotions>
                                ) : undefined}
                            </HeadingContainer>
                        </>
                    ) : (
                        <HeadingContainer>
                            <Text
                                tag={TextTag.h2}
                                appearance={TokenTextAppearance.headline300}
                                textAlign={TextAlignment.center}
                            >
                                <span>
                                    {t(
                                        'teaserView.title.myDealer.noAvailablePromotions'
                                    )}{' '}
                                    <b>{dealerName}</b>
                                </span>
                            </Text>
                            {allPromotions.length >
                            myDealerPromotions.length ? (
                                <CTAAllPromotions
                                    tag="button"
                                    emphasis="secondary"
                                    title={t('teaserView.cta.text')}
                                    aria-label={t('teaserView.cta.text')}
                                    onClick={() => {
                                        setShowAllPromotions(true)
                                    }}
                                >
                                    {t('teaserView.cta.text')}
                                </CTAAllPromotions>
                            ) : undefined}
                        </HeadingContainer>
                    )}
                </>
            ) : !isEmpty(allPromotions) ? (
                <PromotionsTeaserContainer>
                    <PromotionsComponent promotions={allPromotions} />
                </PromotionsTeaserContainer>
            ) : (
                <HeadingContainer>
                    <Text
                        tag={TextTag.h2}
                        appearance={TokenTextAppearance.headline300}
                        textAlign={TextAlignment.center}
                    >
                        <span>
                            {t('teaserView.title.noAvailablePromotions')}
                        </span>
                    </Text>
                </HeadingContainer>
            )}
        </>
    )
}
